import { useState } from 'react';
import {
  useBillingStatus,
  useCreateCheckout,
  useCreatePortal,
  useInvoices,
  useUpdateOverage,
} from '../../api/hooks/billing.hook';

// ── Plan Card ────────────────────────────────────────────────────────

const PLAN_DISPLAY: Record<string, { label: string; color: string }> = {
  free: { label: 'Free', color: 'bg-neutral-100 text-neutral-600' },
  pro: { label: 'Pro', color: 'bg-blue-100 text-blue-700' },
  elite: { label: 'Elite', color: 'bg-purple-100 text-purple-700' },
  enterprise: { label: 'Enterprise', color: 'bg-amber-100 text-amber-700' },
};

const PlanBadge = ({ plan }: { plan: string }) => {
  const info = PLAN_DISPLAY[plan] ?? PLAN_DISPLAY.free;
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${info.color}`}>
      {info.label}
    </span>
  );
};

// ── Usage Bar ────────────────────────────────────────────────────────

const UsageBar = ({
  used,
  limit,
  label,
}: {
  used: number;
  limit: number;
  label: string;
}) => {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const barColor =
    pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-blue-500';

  return (
    <div>
      <div className="flex justify-between text-xs text-neutral-500 mb-1">
        <span>{label}</span>
        <span>
          {used.toLocaleString()} / {limit > 0 ? limit.toLocaleString() : 'Unlimited'}
        </span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ── Upgrade Panel ────────────────────────────────────────────────────

const UpgradePanel = ({ currentPlan, billingEnabled }: { currentPlan: string; billingEnabled: boolean }) => {
  const checkout = useCreateCheckout();
  const [selectedPlan, setSelectedPlan] = useState<'pro' | 'elite'>('pro');
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [error, setError] = useState<string | null>(null);

  if (!billingEnabled) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
        <h2 className="text-sm font-medium text-neutral-700 mb-2">
          Upgrade Plan
        </h2>
        <p className="text-xs text-neutral-400">
          Billing is not configured. Contact your administrator to enable Stripe billing.
        </p>
      </div>
    );
  }

  if (currentPlan === 'enterprise') return null;

  const handleUpgrade = async () => {
    setError(null);
    try {
      const result = await checkout.mutateAsync({
        plan: selectedPlan,
        billing_cycle: cycle,
        success_url: `${window.location.origin}/admin/billing?success=true`,
        cancel_url: `${window.location.origin}/admin/billing?cancelled=true`,
      });
      window.location.href = result.checkout_url;
    } catch {
      setError('Failed to create checkout session.');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
      <h2 className="text-sm font-medium text-neutral-700 mb-3">
        Upgrade Plan
      </h2>
      <div className="flex gap-3 items-end">
        <div>
          <label className="block text-xs text-neutral-500 mb-1">Plan</label>
          <select
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value as 'pro' | 'elite')}
            className="border border-neutral-200 rounded px-3 py-1.5 text-sm bg-white"
          >
            {currentPlan === 'free' && <option value="pro">Pro</option>}
            <option value="elite">Elite</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-1">Billing Cycle</label>
          <select
            value={cycle}
            onChange={(e) => setCycle(e.target.value as 'monthly' | 'yearly')}
            className="border border-neutral-200 rounded px-3 py-1.5 text-sm bg-white"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly (save 20%)</option>
          </select>
        </div>
        <button
          type="button"
          onClick={handleUpgrade}
          disabled={checkout.isLoading}
          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {checkout.isLoading ? 'Redirecting...' : 'Upgrade'}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
};

// ── Invoices Panel ───────────────────────────────────────────────────

const InvoicesPanel = () => {
  const { data, isLoading } = useInvoices();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
        <h2 className="text-sm font-medium text-neutral-700 mb-2">Invoices</h2>
        <p className="text-xs text-neutral-400">Loading...</p>
      </div>
    );
  }

  const invoices = data?.invoices ?? [];

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h2 className="text-sm font-medium text-neutral-700">Invoices</h2>
      </div>
      {invoices.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide border-b border-neutral-50">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">PDF</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr
                key={inv.id}
                className="border-b border-neutral-50 hover:bg-neutral-50/50"
              >
                <td className="px-4 py-2.5 text-neutral-900">
                  {inv.created_at
                    ? new Date(inv.created_at).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-4 py-2.5 text-neutral-700">
                  {(inv.amount_cents / 100).toFixed(2)} {inv.currency.toUpperCase()}
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      inv.status === 'paid'
                        ? 'bg-green-50 text-green-600'
                        : inv.status === 'draft'
                          ? 'bg-neutral-100 text-neutral-500'
                          : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {inv.status}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  {inv.pdf_url ? (
                    <a
                      href={inv.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Download
                    </a>
                  ) : (
                    <span className="text-neutral-300 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="px-4 py-8 text-center text-sm text-neutral-400">
          No invoices yet.
        </div>
      )}
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────

export const BillingPage = () => {
  const { data, isLoading } = useBillingStatus();
  const portal = useCreatePortal();
  const overageMut = useUpdateOverage();

  if (isLoading || !data) {
    return (
      <div className="max-w-4xl">
        <h1 className="text-lg font-semibold text-neutral-900 mb-6">
          Billing & Subscription
        </h1>
        <div className="text-sm text-neutral-400">Loading...</div>
      </div>
    );
  }

  const { subscription, plan_info, usage, billing_enabled } = data;
  const currentPlan = plan_info.plan;

  const handlePortal = async () => {
    try {
      const result = await portal.mutateAsync({
        return_url: `${window.location.origin}/admin/billing`,
      });
      window.location.href = result.portal_url;
    } catch {
      // portal only works if stripe_customer_id exists
    }
  };

  const toggleOverage = async () => {
    if (!subscription) return;
    await overageMut.mutateAsync({
      usage_overage_enabled: !subscription.usage_overage_enabled,
    });
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-lg font-semibold text-neutral-900 mb-2">
        Billing & Subscription
      </h1>
      <p className="text-sm text-neutral-500 mb-6">
        Manage your organization's subscription, view usage, and download
        invoices.
      </p>

      {/* Current Plan Overview */}
      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-neutral-700">
            Current Plan
          </h2>
          <PlanBadge plan={currentPlan} />
        </div>

        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <span className="text-neutral-500 text-xs">Members</span>
            <p className="text-neutral-900 font-medium">
              {plan_info.max_members > 999999
                ? 'Unlimited'
                : plan_info.max_members}
            </p>
          </div>
          <div>
            <span className="text-neutral-500 text-xs">Projects</span>
            <p className="text-neutral-900 font-medium">
              {plan_info.max_projects > 999999
                ? 'Unlimited'
                : plan_info.max_projects}
            </p>
          </div>
          <div>
            <span className="text-neutral-500 text-xs">Monthly Tokens</span>
            <p className="text-neutral-900 font-medium">
              {plan_info.monthly_token_limit > 999999999
                ? 'Unlimited'
                : plan_info.monthly_token_limit.toLocaleString()}
            </p>
          </div>
        </div>

        <UsageBar
          used={usage.total_tokens_used}
          limit={plan_info.monthly_token_limit}
          label="Token Usage (this period)"
        />

        {subscription && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-neutral-400">
              Status: <strong className="text-neutral-600">{subscription.status}</strong>
              {subscription.current_period_end && (
                <>
                  {' '}| Renews:{' '}
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </>
              )}
              {subscription.cancel_at && (
                <span className="text-red-500">
                  {' '}| Cancels:{' '}
                  {new Date(subscription.cancel_at).toLocaleDateString()}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Manage / Portal button */}
        {billing_enabled && subscription?.stripe_subscription_id && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handlePortal}
              disabled={portal.isLoading}
              className="px-4 py-1.5 text-sm font-medium text-neutral-700 bg-neutral-100 rounded hover:bg-neutral-200 disabled:opacity-50 transition-colors"
            >
              {portal.isLoading ? 'Loading...' : 'Manage Subscription'}
            </button>
            <button
              type="button"
              onClick={toggleOverage}
              disabled={overageMut.isLoading}
              className="px-4 py-1.5 text-sm font-medium text-neutral-700 bg-neutral-100 rounded hover:bg-neutral-200 disabled:opacity-50 transition-colors"
            >
              {subscription.usage_overage_enabled
                ? 'Disable Overage'
                : 'Enable Overage'}
            </button>
          </div>
        )}
      </div>

      <UpgradePanel currentPlan={currentPlan} billingEnabled={billing_enabled} />

      <InvoicesPanel />
    </div>
  );
};
